import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Grid, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../store';
import { findReferralSummary } from '../../../store/referralSummary/api';
import Loader from '../../../components/Loader';
import Scrollbar from '../../../components/Scrollbar';
import Label from '../../../components/Label';
import { Users } from '../../../store/users/api/interfaces';
import { fDateTime } from '../../../utils/formatTime';

interface IProps {
  user: Users | undefined;
}

const getInviteStatusColor = (status?: string) => {
  switch (status) {
    case 'invited':
      return 'warning';
    case 'signed_up':
      return 'info';
    case 'first_order':
    case 'completed':
      return 'success';
    default:
      return 'default';
  }
};

const getRewardStatusColor = (status?: string) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'expired':
      return 'error';
    default:
      return 'default';
  }
};

const ReferralTab: React.FC<IProps> = ({ user }) => {
  const dispatch = useAppDispatch();
  const { data, isLoading } = useAppSelector((state) => state.referralSummary);
  const [innerTab, setInnerTab] = useState(0);

  useEffect(() => {
    if (user?.id) {
      dispatch(
        findReferralSummary({
          userId: user.id,
          invitesLimit: 100,
          rewardsLimit: 100,
        })
      );
    }
  }, [dispatch, user]);

  const invites = data?.invites || [];
  const rewards = data?.rewards || [];
  const stats = data?.statistics;

  return (
    <>
      {isLoading ? (
        <Box sx={{ py: 4 }}>
          <Loader />
        </Box>
      ) : (
        <>
          <Grid
            container
            spacing={2}
            sx={{ px: 3, pt: 2 }}
          >
            <Grid
              item
              xs={12}
              md={3}
            >
              <Stack direction='row'>
                <Typography variant='subtitle2'>Friends Invited:</Typography>
                <Typography variant='body2'>
                  &nbsp;{stats?.friendsInvited ?? 0}
                </Typography>
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <Stack direction='row'>
                <Typography variant='subtitle2'>Signed Up:</Typography>
                <Typography variant='body2'>
                  &nbsp;{stats?.signedUp ?? 0}
                </Typography>
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <Stack direction='row'>
                <Typography variant='subtitle2'>First Orders:</Typography>
                <Typography variant='body2'>
                  &nbsp;{stats?.firstOrders ?? 0}
                </Typography>
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <Stack direction='row'>
                <Typography variant='subtitle2'>Earned:</Typography>
                <Typography variant='body2'>
                  &nbsp;{stats?.earnedQar ?? 0} QAR
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <Box sx={{ px: 2, mt: 2 }}>
            <Tabs
              value={innerTab}
              onChange={(_event, newValue) => setInnerTab(newValue)}
              sx={{ minHeight: 48 }}
            >
              <Tab label={'Invites'} />
              <Tab label={'Rewards'} />
            </Tabs>
          </Box>

          {innerTab === 0 ? (
            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative', pt: 3 }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Name'}</TableCell>
                      <TableCell align='center'>{'Mobile'}</TableCell>
                      <TableCell align='center'>{'Date'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invites.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      invites.map((invite) => (
                        <TableRow key={invite.id}>
                          <TableCell align='center'>
                            {invite.fullName || '-'}
                          </TableCell>
                          <TableCell align='center'>
                            {invite.mobileDisplay || '-'}
                          </TableCell>
                          <TableCell align='center'>
                            {invite.dateTimeDisplay ||
                              (invite.invitedAt
                                ? fDateTime(invite.invitedAt)
                                : '-')}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={getInviteStatusColor(invite.status)}
                            >
                              {invite.statusLabel || invite.status || '-'}
                            </Label>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          ) : (
            <Scrollbar>
              <TableContainer sx={{ minWidth: 1000, position: 'relative', pt: 3 }}>
                <Table size={'medium'}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Name'}</TableCell>
                      <TableCell align='center'>{'Points'}</TableCell>
                      <TableCell align='center'>{'Amount'}</TableCell>
                      <TableCell align='center'>{'Date'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rewards.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      rewards.map((reward) => (
                        <TableRow key={reward.id}>
                          <TableCell align='center'>
                            {reward.refereeName || '-'}
                          </TableCell>
                          <TableCell align='center'>
                            {reward.points ?? 0}
                          </TableCell>
                          <TableCell align='center'>
                            {reward.amountDisplay ||
                              `${reward.amountQar ?? 0} QAR`}
                          </TableCell>
                          <TableCell align='center'>
                            {reward.dateTimeDisplay || '-'}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={getRewardStatusColor(reward.status)}
                            >
                              {reward.statusLabel || reward.status || '-'}
                            </Label>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </>
      )}
    </>
  );
};

export default ReferralTab;
